import { Loader2, Save, Trash2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { RuleDefinition } from '@/types';
import { CodeField } from './CodeField';
import { ListsEditor } from './ListsEditor';
import type { SuggestModel } from '../lib/autocomplete';

const SEVERITIES: RuleDefinition['severity'][] = ['critical', 'warning', 'info'];
const SEV_STYLE: Record<string, string> = {
  critical: 'border-crit/40 bg-crit-bg text-crit',
  warning: 'border-warn/40 bg-warn-bg text-warn',
  info: 'border-info/40 bg-info-bg text-info',
};

export function RuleEditor({
  draft,
  onChange,
  onSave,
  onDelete,
  saving,
  deleting,
  dirty,
  isExisting,
  saveError,
  expressionError,
  templateError,
  model,
}: {
  draft: RuleDefinition;
  onChange: (patch: Partial<RuleDefinition>) => void;
  onSave: () => void;
  onDelete: () => void;
  saving: boolean;
  deleting: boolean;
  dirty: boolean;
  isExisting: boolean;
  saveError?: string;
  expressionError?: string;
  templateError?: string;
  /** Drives editor autocomplete (real bundle when a sample is loaded, else static). */
  model: SuggestModel;
}) {
  const canSave =
    Boolean(draft.id.trim() && draft.name.trim() && draft.expression.trim() && draft.message.trim()) &&
    !expressionError &&
    !templateError;

  return (
    <div className="flex h-full flex-col overflow-auto">
      <div className="space-y-4 p-4">
        <div className="grid grid-cols-[1fr_1fr_auto] gap-3">
          <TextField
            label="rule id"
            value={draft.id}
            onChange={(v) => onChange({ id: slug(v) })}
            mono
            placeholder="undelivered-win"
            readOnly={isExisting}
            hint={isExisting ? 'fixed once saved' : 'lowercase-slug'}
          />
          <TextField
            label="name"
            value={draft.name}
            onChange={(v) => onChange({ name: v })}
            placeholder="Win never delivered"
          />
          <div>
            <div className="eyebrow mb-1">severity</div>
            <div className="flex border border-hairline">
              {SEVERITIES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onChange({ severity: s })}
                  className={cn(
                    'border-r border-hairline px-2 py-1.5 font-condensed text-[10px] font-semibold uppercase tracking-wider last:border-r-0',
                    draft.severity === s ? SEV_STYLE[s!] : 'text-text-faint hover:text-text-dim',
                  )}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        <CodeField
          label="expression"
          hint="JSONata over { bundle }, $lists — a truthy result fires the rule and becomes `matched`"
          value={draft.expression}
          onChange={(v) => onChange({ expression: v })}
          error={expressionError}
          touched={Boolean(draft.expression.trim())}
          rows={5}
          placeholder="( $r := bundle.transactions.data[action='win' and rollbacked=1]; $count($r) > 0 ? { 'id': $r[0].id } : undefined )"
          mode="jsonata"
          model={model}
        />

        <CodeField
          label="message"
          hint="LiquidJS, rendered with { bundle, matched, lists }"
          value={draft.message}
          onChange={(v) => onChange({ message: v })}
          error={templateError}
          touched={Boolean(draft.message.trim())}
          rows={3}
          placeholder="Win {{ matched.id }} for bet {{ bundle.resolved.bet_id }} was rolled back."
          mode="liquid"
          model={model}
        />

        <ListsEditor lists={draft.lists ?? {}} onChange={(lists) => onChange({ lists })} />
      </div>

      <div className="sticky bottom-0 mt-auto flex items-center gap-2 border-t border-hairline bg-surface px-4 py-2">
        <button
          type="button"
          onClick={onSave}
          disabled={!canSave || saving || (!dirty && isExisting)}
          className={cn(
            'inline-flex items-center gap-1.5 border border-accent/40 bg-accent/10 px-3 py-1.5',
            'font-condensed text-[11px] font-semibold uppercase tracking-wider text-accent',
            'hover:bg-accent/20 disabled:cursor-not-allowed disabled:opacity-40',
          )}
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          {isExisting ? 'Save changes' : 'Create rule'}
        </button>

        {isExisting && (
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="inline-flex items-center gap-1.5 border border-hairline px-3 py-1.5 font-condensed text-[11px] font-semibold uppercase tracking-wider text-text-dim hover:border-crit/40 hover:text-crit disabled:opacity-40"
          >
            {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            Delete
          </button>
        )}

        {dirty && <span className="font-condensed text-[10px] uppercase tracking-wider text-text-faint">unsaved</span>}
        {saveError && <span className="ml-auto font-mono text-[11px] text-crit">{saveError}</span>}
      </div>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  mono,
  placeholder,
  readOnly,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  hint?: string;
}) {
  return (
    <div>
      <div className="eyebrow mb-1">
        {label}
        {hint && <span className="ml-1.5 normal-case tracking-normal text-text-faint">· {hint}</span>}
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        spellCheck={false}
        className={cn(
          'w-full border border-hairline bg-surface-sunken px-2 py-1.5 text-[12px] text-text',
          'placeholder:text-text-faint focus:border-accent focus:outline-none',
          mono && 'font-mono',
          readOnly && 'opacity-60',
        )}
      />
    </div>
  );
}

function slug(v: string): string {
  return v.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-/, '');
}
